import { useMemo } from "react";
import {
  type StableWebSocketOptions,
  useStableWebSocket,
} from "./useStableWebSocket";

declare const __API_URL__: string;

interface UseAppPartySocketOptions extends StableWebSocketOptions {
  room?: string;
  party?: string;
  // PartySocket specific options
  query?: Record<string, string>;
}

/**
 * App-specific WebSocket hook that connects to PartyServer infrastructure.
 * Uses the stable WebSocket implementation to prevent thrashing.
 */
export function useAppPartySocket(options: UseAppPartySocketOptions) {
  const { room, party, query, ...webSocketOptions } = options;

  const isDisabled = !room || room === "disabled";

  if (party && !room && !isDisabled) {
    console.error("[PartySocket] Both party and room must be specified");
    throw new Error(
      "Both party and room must be specified when connecting to PartySocket",
    );
  }

  // Memoize the WebSocket URL to prevent thrashing
  // This is critical for the share: true option to work properly
  const webSocketUrl = useMemo(() => {
    if (isDisabled) {
      console.log("[PartySocket] Socket disabled or no room specified");
      return null;
    }

    const apiUrl = new URL(__API_URL__);
    const protocol = apiUrl.protocol === "https:" ? "wss:" : "ws:";
    const host = apiUrl.host;

    // Build path: /parties/{party}/{room} or /room/{room}
    const path = party ? `/parties/${party}/${room}` : `/room/${room}`;

    // Build query string
    const queryParams = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        queryParams.set(key, value);
      });
    }

    const queryString = queryParams.toString();
    const url = `${protocol}//${host}${path}${queryString ? `?${queryString}` : ""}`;

    console.log(`🔌 Connecting to PartySocket: ${url}`);
    return url;
  }, [isDisabled, party, room, query]);

  // Always call the hook, but with null URL when disabled
  const webSocket = useStableWebSocket(webSocketUrl, {
    ...webSocketOptions,
    // PartySocket specific configuration
    deploymentGracePeriod: 8000, // PartyServer restarts can take a bit longer
    maxReconnectAttempts: 15, // More attempts for critical party connections
  });

  // Create backward-compatible interface
  const compatibleSocket = {
    ...webSocket,
    // PartySocket-compatible properties
    ws: webSocket.getWebSocket(),
    room: room ?? "",
    party,

    // Backward compatibility methods
    send: (message: string) => webSocket.sendMessage(message),
    close: () => {
      const ws = webSocket.getWebSocket();
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    },
    reconnect: () => {
      const ws = webSocket.getWebSocket();
      if (ws) {
        ws.close(1000, "Manual reconnect");
      }
    },
  };

  return compatibleSocket;
}
